import React, { useContext, useEffect } from 'react';
import PageHeader from '../components/common/PageHeader';
import { NotificationContext } from '../context/NotificationContext';
import { formatDateTime } from '../utils/formatters';
import communicationService from '../services/communicationService';
import toast from 'react-hot-toast';

function humanize(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'number') return v.toLocaleString('en-KE');
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

export default function Notifications() {
  const {
    notifications,
    loadNotifications,
    markRead,
    markAllRead,
    unreadCount,
    communicationPrefs,
    prefsSaving,
    updateCommunicationPreferences,
  } = useContext(NotificationContext);

  useEffect(() => { loadNotifications(); }, []);

  const toggleEmailNotifications = async (e) => {
    const enabled = e.target.checked;
    try {
      await updateCommunicationPreferences({ email_notifications_enabled: enabled });
      toast.success(enabled ? 'Email notifications enabled' : 'Email notifications disabled');
    } catch {
      toast.error('Could not update email notification setting');
    }
  };

  const sendEmailNow = async (notificationId) => {
    try {
      await communicationService.sendNotificationEmail(notificationId);
      toast.success('Notification emailed');
    } catch {
      toast.error('Email delivery endpoint is unavailable');
    }
  };

  return (
    <div>
      <PageHeader
        title="Notifications"
        subtitle={`${notifications.length} total notifications • ${unreadCount} unread`}
        actions={(
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--gray-600)' }}>
              <input
                type="checkbox"
                checked={Boolean(communicationPrefs?.email_notifications_enabled)}
                onChange={toggleEmailNotifications}
                disabled={prefsSaving}
              />
              Email on notification
            </label>
            <button className="btn btn-secondary" onClick={markAllRead}>Mark all read</button>
          </div>
        )}
      />

      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {notifications.length === 0 ? (
            <div style={{ padding: '1.5rem', color: 'var(--gray-500)' }}>No notifications yet.</div>
          ) : notifications.map(n => (
            <div
              key={n.id}
              onClick={() => markRead(n.id)}
              style={{
                padding: '1rem 1.25rem',
                borderBottom: '1px solid var(--gray-100)',
                background: n.is_read ? 'transparent' : 'var(--gray-50)',
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center' }}>
                <div style={{ fontWeight: n.is_read ? 500 : 700, fontSize: '0.92rem' }}>{n.title || 'Notification'}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{formatDateTime(n.created_at)}</div>
              </div>
              <div style={{ marginTop: '0.3rem', fontSize: '0.875rem', color: 'var(--gray-600)' }}>{n.message}</div>

              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.45rem', flexWrap: 'wrap' }}>
                {n.type && <span className="badge badge-info">{n.type}</span>}
                {n.event_code && <span className="badge badge-neutral">{n.event_code}</span>}
                {n.actor_name && <span className="badge badge-neutral">Actor: {n.actor_name}</span>}
                {n.entity_type && <span className="badge badge-neutral">{n.entity_type} #{n.entity_id || '—'}</span>}
              </div>

              {n.details && Object.keys(n.details).length > 0 && (
                <div style={{ marginTop: '0.6rem', background: 'var(--gray-50)', border: '1px solid var(--gray-100)', borderRadius: 'var(--radius-md)', padding: '0.6rem 0.75rem' }}>
                  {Object.entries(n.details).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', fontSize: '0.78rem', padding: '0.15rem 0' }}>
                      <span style={{ color: 'var(--gray-500)', textTransform: 'capitalize' }}>{k.replaceAll('_', ' ')}</span>
                      <span style={{ fontWeight: 500, color: 'var(--gray-700)', textAlign: 'right' }}>{humanize(typeof v === 'object' ? JSON.stringify(v) : v)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: '0.65rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    sendEmailNow(n.id);
                  }}
                >
                  Send Email
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
